import { ReportElement } from './report-element';
import { ReportUnits } from './report-units';
import { ReportFonts } from './report-fonts';
import { TemplateImage, ElementLocationInSection } from './report-template';
import { ElementType } from './element-type';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { Subscription } from 'rxjs';
import { DYNAMIC_SELECT_ITEM } from '../app.constants';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';
import { ReportPaperSize } from './report-paper-size';
import { ReportPageMargin } from './report-page-margin';
import { ReportCommonSection } from './report-common-section';

export class ReportImage extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;
  public src: string;
  public dynamicDataField: string;
  public dynamicId: string;
  private units: ReportUnits;
  private fonts: ReportFonts;
  public isCustomElementDraw?:boolean;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  private baseDef: TemplateImage;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaType = 'type';
  private readonly metaSrc = 'src';
  private readonly metaDynamicId = 'dynamicId';

  constructor(index: number, units: ReportUnits, fonts: ReportFonts, 
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    paperSize: ReportPaperSize, 
    pageMargin: ReportPageMargin,
    header: ReportCommonSection, 
    footer: ReportCommonSection, 
    templateImage?: TemplateImage) {

    super(ElementType.Image, index, !(templateImage == null), translate, langService, templateImage ? templateImage.id : null);

    this.units = units;
    this.fonts = fonts;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;
    this.header = header;
    this.footer = footer;

    if (templateImage) {
      this.baseDef = templateImage;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        src: null,
        type: 'static',
        dynamicDataField: '',
        dynamicId: null
      };
    }

    this.zIndex = this.baseDef.zIndex;
    this.src = this.baseDef.src;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.getWidth();
    this.height = this.getHeight();

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.getWidthValue(metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.getHeightValue(metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    if (this.baseDef[this.metaWidth] !== null && this.baseDef[this.metaWidth] < 0) {
      return this.units.toPx('sizeUnit', 1);
    } else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
    }
  }
  public getHeightPx(): number {
    if (this.baseDef[this.metaHeight] !== null && this.baseDef[this.metaHeight] < 0) {
      return this.units.toPx('sizeUnit', 0); // auto
    } else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
    }
  }

  public getTemplateJSON(): TemplateImage {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaSrc, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaDynamicId, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if(metadata.value === 'dynamic' && this.baseDef.dynamicId === 'photoGallery') {
          this.resizeImage();
        }
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaSrc:
        this.baseDef[metadata.name] = metadata.value as string;
        this.src = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDynamicId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.src = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if(metadata.value === 'photoGallery' && this.baseDef.type !== 'static') {
          this.resizeImage();
        }
        break;  
    }
  }

  private resizeImage() : void {
    const heightNum = this.paperSize.getHeightNum() - this.pageMargin.getTopNum() - this.pageMargin.getBottomNum() - this.header.getHeightNum() - this.footer.getHeightNum();
    const widthNum = this.paperSize.getWidthNum() - this.pageMargin.getRightNum() - this.pageMargin.getLeftNum();
    this.top = this.units.concatenate('sizeUnit', 0 as number);
    this.left = this.units.concatenate('sizeUnit', 0 as number);
    this.height = this.units.concatenate('sizeUnit', heightNum as number);
    this.width = this.units.concatenate('sizeUnit', widthNum as number);
    this.setPreviewValue(this.metaLeft, 0);
    this.setPreviewValue(this.metaTop, 0);
    this.setPreviewValue(this.metaWidth, widthNum);
    this.setPreviewValue(this.metaHeight, heightNum);
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: 0,
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: 0,
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: widthNum,
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: heightNum,
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
      })
    );
  }

  private getHeight(): string {
    if (
      this.baseDef.height !== null &&
      this.baseDef.height !== undefined &&
      this.baseDef.height > 0
    ) {
      return this.units.concatenate('sizeUnit', this.baseDef.height);
    } else {
      return 'auto';
    }
  }

  private getWidth(): string {
    if (
      this.baseDef.width !== null &&
      this.baseDef.width !== undefined &&
      this.baseDef.width > 0
    ) {
      return this.units.concatenate('sizeUnit', this.baseDef.width);
    } else {
      return this.units.concatenate('sizeUnit', 1);
    }
  }

  private getHeightValue(value: number): number {
    if (value !== null && value > 0) {
      return this.units.fromPx('sizeUnit', value);
    } else {
      return this.units.fromPx('sizeUnit', 0);
    }
  }

  private getWidthValue(value: number): number {
    if (value !== null && value > 0) {
      return this.units.fromPx('sizeUnit', value);
    } else {
      return this.units.fromPx('sizeUnit', 1);
    }
  }

  public isCustomElement() {
    return this.baseDef['isCustomElementDraw'];
   }
}
